import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  A4_CONTENT_W,
  A4_FIT_H,
  A4_FULL_H,
  A4_FULL_W,
  A4_MARGIN_PX,
  A4_PRINT_TRIM,
  A4_SCALE,
  COPIES_DEFAULT,
  COPIES_MAX,
  COPIES_MIN,
  DIVIDER_H,
  FONT_DEFAULT,
  FONT_MAX,
  FONT_MIN,
  LEADING_MAX,
  LEADING_MIN,
  LEADING_STEP,
  SHEET_CSS,
  buildQuestionPaperPrintHtml,
  chooseHalfCut,
  clampCopies,
  clampFont,
  clampLeading,
  collectStyles,
  copiesThatFit,
  defaultLeading,
  packSheets,
  paperFontFamily,
  styleKey
} from '../utils/questionPaperPrint';
import FractionText from './FractionText';
import PassageContent from './PassageContent';
import './QuestionPaperPrintPreview.css';

const DISP_W = Math.round(A4_FULL_W * A4_SCALE);
const DISP_H = Math.round(A4_FULL_H * A4_SCALE);

function BlockView({ block }) {
  if (block.type === 'section') {
    return <h2 className="qpp-section">{block.text}</h2>;
  }
  if (block.type === 'passage') {
    return (
      <div className="qpp-passage">
        <PassageContent
          lines={String(block.text || '').split('\n')}
          parts={block.passageParts}
          underClass="qpp-ul"
          tableClass="qpp-data"
        />
      </div>
    );
  }
  const parts = block.restParts && block.restParts.length
    ? block.restParts
    : [{ text: block.rest || '', under: false }];
  const passage = block.passage && block.passage.length
    ? (
      <div className="qpp-passage">
        <PassageContent
          lines={block.passage}
          parts={block.passageParts}
          underClass="qpp-ul"
          tableClass="qpp-data"
        />
      </div>
    )
    : null;
  return (
    <>
      <p className="qpp-question">
        {block.label ? <span className="qpp-q-label">{block.label}</span> : null}
        {block.label ? ` ${block.sep || '-'} ` : ''}
        {parts.map((bit, index) => (
          bit.under
            ? <span key={index} className="qpp-ul"><FractionText text={bit.text} /></span>
            : <span key={index}><FractionText text={bit.text} /></span>
        ))}
      </p>
      {passage}
    </>
  );
}

function PaperHeader({ paper, rtl }) {
  const showMeta = paper.className || paper.subject || paper.dateLabel || paper.totalMarks;
  return (
    <div data-measure="header">
      {showMeta ? (
        <div className="qpp-meta">
          <div className="qpp-meta-side">
            {paper.className ? <span>{rtl ? 'جماعت' : 'Class'}: {paper.className}</span> : null}
            {paper.subject ? <span>{rtl ? 'مضمون' : 'Subject'}: {paper.subject}</span> : null}
          </div>
          <div className="qpp-total">
            {paper.totalMarks ? `${rtl ? 'کل نمبر' : 'Total Marks'}: ${paper.totalMarks}` : ''}
          </div>
          <div className="qpp-meta-side qpp-meta-side--end">
            {paper.dateLabel ? <span>{rtl ? 'تاریخ' : 'Date'}: {paper.dateLabel}</span> : null}
          </div>
        </div>
      ) : null}
      <div className="qpp-name">
        <span>{rtl ? 'رول نمبر' : 'Roll Number'}:</span>
        <span>{rtl ? 'نام' : 'Name'}:</span>
      </div>
      <div className="qpp-name-gap" />
    </div>
  );
}

function PaperFace({ paper, rtl, side, blocks }) {
  return (
    <>
      {side === 'front' ? <PaperHeader paper={paper} rtl={rtl} /> : null}
      {blocks.map((block, index) => <BlockView key={`${side}-${index}`} block={block} />)}
    </>
  );
}

function Stepper({ label, ariaLabel, className, onDecrease, onIncrease, decreaseDisabled, increaseDisabled, children }) {
  const name = ariaLabel || label;
  return (
    <div className={className || 'qpp-stepper'}>
      <span className="qpp-stepper-label">{label}</span>
      <button
        type="button"
        className="qpp-step"
        onClick={onDecrease}
        disabled={decreaseDisabled}
        aria-label={`Decrease ${name}`}
      >
        −
      </button>
      {children}
      <button
        type="button"
        className="qpp-step"
        onClick={onIncrease}
        disabled={increaseDisabled}
        aria-label={`Increase ${name}`}
      >
        +
      </button>
    </div>
  );
}

function printLaidOutPages() {
  const nodes = Array.from(document.querySelectorAll('[data-qp-print-page]'));
  if (!nodes.length || typeof document === 'undefined') return;
  const sheets = nodes.map((node) => {
    const clone = node.cloneNode(true);
    clone.style.transform = 'none';
    clone.style.width = `${A4_FULL_W}px`;
    clone.style.height = `${A4_FULL_H - A4_PRINT_TRIM}px`;
    clone.style.overflow = 'hidden';
    clone.style.margin = '0';
    clone.style.boxShadow = 'none';
    return clone.outerHTML;
  }).join('');
  const html = buildQuestionPaperPrintHtml(sheets);
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  const win = iframe.contentWindow;
  const doc = win.document;
  doc.open();
  doc.write(html);
  doc.close();
  const cleanup = () => {
    try { iframe.remove(); } catch (_) { /* already gone */ }
  };
  const go = () => {
    win.focus();
    win.print();
    win.onafterprint = cleanup;
    setTimeout(cleanup, 1500);
  };
  if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(go).catch(go);
  else setTimeout(go, 300);
}

export default function QuestionPaperPrintPreview({ paper, onClose }) {
  const rtl = paper.dir === 'rtl';
  const blocks = paper.blocks || [];
  const fontFamily = paperFontFamily(paper.dir);
  const [fontPt, setFontPt] = useState(FONT_DEFAULT);
  const [leading, setLeading] = useState(() => defaultLeading(paper.dir));
  const [copies, setCopies] = useState(COPIES_DEFAULT);
  const [copiesText, setCopiesText] = useState(String(COPIES_DEFAULT));
  const [metrics, setMetrics] = useState({});
  const [fontEpoch, setFontEpoch] = useState(0);
  const measureRootRef = useRef(null);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let cancel = false;
    const fonts = document.fonts;
    const ready = fonts && fonts.ready;
    if (fonts && fonts.status === 'loaded') return undefined;
    if (ready && typeof ready.then === 'function') {
      ready.then(() => {
        if (!cancel) setFontEpoch((value) => value + 1);
      });
    }
    return () => { cancel = true; };
  }, [paper]);

  const styleList = collectStyles(fontPt, leading);
  const measureSig = styleList.map((style) => {
    const metric = metrics[style.key];
    return metric ? `${style.key}:${metric.cut}:${metric.measured ? 1 : 0}` : `${style.key}:pending`;
  }).join(',');

  useLayoutEffect(() => {
    const root = measureRootRef.current;
    if (!root) return;
    const columns = Array.from(root.querySelectorAll('[data-qpp-style]'));
    setMetrics((prev) => {
      let changed = false;
      const next = {};
      columns.forEach((column) => {
        const key = column.getAttribute('data-qpp-style');
        const blocksRoot = column.querySelector('[data-qpp-blocks]');
        if (!key || !blocksRoot) return;
        const header = blocksRoot.querySelector('[data-measure="header"]');
        const headerHeight = header ? header.offsetHeight : 0;
        const heights = blocks.map((_, index) => {
          const node = blocksRoot.querySelector(`[data-block-index="${index}"]`);
          return node ? node.offsetHeight : 0;
        });
        const cut = chooseHalfCut(heights, headerHeight, blocks);
        const previous = prev[key];
        let slot = previous ? previous.slot : 1;
        let perPage = previous ? previous.perPage : 1;
        let overflow = previous ? previous.overflow : false;
        let measured = false;
        if (previous && previous.cut === cut) {
          const front = column.querySelector('[data-qpp-face="front"]');
          const back = column.querySelector('[data-qpp-face="back"]');
          if (front && back) {
            const raw = Math.max(front.offsetHeight, back.offsetHeight);
            slot = raw > 0 ? raw : 1;
            perPage = copiesThatFit(slot, A4_FIT_H, DIVIDER_H);
            overflow = raw > A4_FIT_H;
            measured = true;
          }
        }
        next[key] = { cut, slot, perPage, overflow, measured };
        if (!previous || previous.cut !== cut || previous.slot !== slot || previous.perPage !== perPage || previous.overflow !== overflow || previous.measured !== measured) {
          changed = true;
        }
      });
      if (!changed && Object.keys(prev).length === Object.keys(next).length) return prev;
      return next;
    });
  }, [blocks, measureSig, fontEpoch]);

  const sheetKey = styleKey(fontPt, leading);
  const allMeasured = Boolean(metrics[sheetKey] && metrics[sheetKey].measured);
  const groups = allMeasured
    ? packSheets(copies, () => metrics[sheetKey].perPage).map((group) => ({
      ...group,
      fontPt,
      leading,
      ...metrics[sheetKey]
    }))
    : [];
  const pageCount = groups.length * 2;
  const overflowPages = groups.filter((group) => group.overflow).map((group) => group.sheet * 2 + 1);

  const commitCopies = (raw) => {
    if (!/^\d+$/.test(String(raw))) return;
    const n = parseInt(raw, 10);
    if (n >= COPIES_MIN && n <= COPIES_MAX) setCopies(n);
  };

  const setCopyCount = (n) => {
    const next = clampCopies(n);
    setCopies(next);
    setCopiesText(String(next));
  };

  const renderPage = (group, side) => {
    const faceBlocks = side === 'front' ? blocks.slice(0, group.cut) : blocks.slice(group.cut);
    return (
      <div className="qpp-frame" style={{ width: DISP_W, height: DISP_H }}>
        <div
          className="qpp-sheet"
          data-qp-print-page="true"
          data-side={side}
          dir={rtl ? 'rtl' : 'ltr'}
          style={{
            fontSize: `${group.fontPt}pt`,
            lineHeight: group.leading,
            fontFamily,
            boxSizing: 'border-box',
            width: A4_FULL_W,
            height: A4_FULL_H,
            padding: A4_MARGIN_PX,
            transform: `scale(${A4_SCALE})`,
            transformOrigin: 'top left',
            background: '#fff',
            overflow: 'hidden',
            color: '#111',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {Array.from({ length: group.count }, (_, index) => {
            const copyNo = group.start + index;
            return (
              <div key={copyNo} style={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                <div style={{ minHeight: group.slot, display: 'flex', flexDirection: 'column' }}>
                  <PaperFace paper={paper} rtl={rtl} side={side} blocks={faceBlocks} />
                </div>
                {index < group.count - 1 && (
                  <div style={{ height: DIVIDER_H, display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '100%', borderTop: '1px solid #000' }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {group.overflow && <div className="qpp-overflow">Exceeds A4</div>}
      </div>
    );
  };

  return createPortal(
    <div
      className="qpp-overlay"
      onClick={(event) => {
        event.stopPropagation();
        onClose();
      }}
    >
      <style>{SHEET_CSS}</style>
      <div
        className="qpp-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Question paper print preview"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="qpp-header">
          <div className="qpp-header-row">
            <h3 className="qpp-title">
              Print Preview ({copies} {copies === 1 ? 'copy' : 'copies'}{allMeasured ? `, ${pageCount} pages` : ', fitting…'})
            </h3>
            <div className="qpp-controls">
              <Stepper
                label="Line spacing"
                onDecrease={() => setLeading((value) => clampLeading(value - LEADING_STEP))}
                onIncrease={() => setLeading((value) => clampLeading(value + LEADING_STEP))}
                decreaseDisabled={leading <= LEADING_MIN}
                increaseDisabled={leading >= LEADING_MAX}
              >
                <span className="qpp-stepper-value" aria-label="Line spacing value">{leading.toFixed(1)}</span>
              </Stepper>
              <Stepper
                label="Font (pt)"
                onDecrease={() => setFontPt((value) => clampFont(value - 1))}
                onIncrease={() => setFontPt((value) => clampFont(value + 1))}
                decreaseDisabled={fontPt <= FONT_MIN}
                increaseDisabled={fontPt >= FONT_MAX}
              >
                <span className="qpp-stepper-value" aria-label="Font size">{fontPt}</span>
              </Stepper>
              <Stepper
                label="Copies"
                onDecrease={() => setCopyCount(copies - 1)}
                onIncrease={() => setCopyCount(copies + 1)}
                decreaseDisabled={copies <= COPIES_MIN}
                increaseDisabled={copies >= COPIES_MAX}
              >
                <input
                  className="qpp-copies"
                  type="number"
                  min={COPIES_MIN}
                  max={COPIES_MAX}
                  aria-label="Number of copies"
                  value={copiesText}
                  onChange={(event) => {
                    setCopiesText(event.target.value);
                    commitCopies(event.target.value);
                  }}
                  onBlur={() => {
                    const next = clampCopies(copiesText);
                    setCopies(next);
                    setCopiesText(String(next));
                  }}
                />
              </Stepper>
              <button
                type="button"
                className="qpp-print"
                onClick={printLaidOutPages}
                disabled={!allMeasured}
              >
                <i className="fas fa-print" style={{ marginRight: 6 }} />
                Print
              </button>
              <button type="button" className="qpp-close" onClick={onClose} aria-label="Close print preview">
                ×
              </button>
            </div>
          </div>
          <div className={overflowPages.length ? 'qpp-tip qpp-tip--warn' : 'qpp-tip'}>
            {overflowPages.length
              ? 'A copy is taller than A4. Reduce the font size or the line spacing.'
              : allMeasured
                ? 'Print double-sided so each back lands on the reverse of its front.'
                : 'Fitting the paper onto A4…'}
          </div>
        </div>

        <div className="qpp-pages">
          {!allMeasured && <div className="qpp-fitting">Fitting pages to A4…</div>}
          {allMeasured && groups.map((group) => (
            <div className="qpp-pair" key={group.sheet}>
              {renderPage(group, 'front')}
              {renderPage(group, 'back')}
            </div>
          ))}
        </div>

        <div className="qpp-measure" aria-hidden="true" ref={measureRootRef}>
          {styleList.map((style) => {
            const metric = metrics[style.key];
            const cut = metric ? metric.cut : null;
            const measureStyle = {
              fontSize: `${style.fontPt}pt`,
              lineHeight: style.leading,
              fontFamily,
              boxSizing: 'border-box',
              width: A4_CONTENT_W,
              background: '#fff',
              color: '#111'
            };
            return (
              <div key={style.key} data-qpp-style={style.key} className="qpp-sheet" dir={rtl ? 'rtl' : 'ltr'} style={measureStyle}>
                <div data-qpp-blocks="true">
                  <PaperHeader paper={paper} rtl={rtl} />
                  {blocks.map((block, index) => (
                    <div key={index} data-block-index={index}>
                      <BlockView block={block} />
                    </div>
                  ))}
                </div>
                {cut != null && (
                  <>
                    <div data-qpp-face="front" style={{ display: 'flex', flexDirection: 'column' }}>
                      <PaperFace paper={paper} rtl={rtl} side="front" blocks={blocks.slice(0, cut)} />
                    </div>
                    <div data-qpp-face="back" style={{ display: 'flex', flexDirection: 'column' }}>
                      <PaperFace paper={paper} rtl={rtl} side="back" blocks={blocks.slice(cut)} />
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}
